import { useEffect } from 'react';
import { ACTION_TYPES, usePurchaseItemResultDispatch, usePurchaseItemResultState } from './purchase-item-result-context';
import { useBaseLayout } from '../../../../../components/context/use-base-layout';
import BaseAppService from '../../../../../application/platform/scp/base/base-app-service';
import PurchaseAppService from '../../../../../application/platform/scp/purchase/purchase-app-service';
import { IAddPurchaseItemResultRequest } from '../../../../../model/scp/purchase-item-result/request/add-purchase-item-result-request';
import { IUpdatePurchaseItemResultRequest } from '../../../../../model/scp/purchase-item-result/request/update-purchase-item-result-request';

export const usePurchaseItemResultController = (onSave: () => void, onClose: () => void) => {
    const { showMessage, showLoading } = useBaseLayout();
    const dispatch = usePurchaseItemResultDispatch();
    const state = usePurchaseItemResultState();

    useEffect(() => {
        if (state.resultData) {
            dispatch({ type: ACTION_TYPES.setIsEditMode, payload: true });
            const updatePayload: IUpdatePurchaseItemResultRequest = {
                approvedQuantity: state.resultData.approvedQuantity || 0,
                approvedUnitPrice: state.resultData.approvedUnitPrice || 0,
                approvedTotalPrice: state.resultData.approvedTotalPrice || 0,
                discountPercentage: state.resultData.discountPercentage || 0,
                itemResultStatusId: state.resultData.itemResultStatusId || 1,
                personTypeId: state.resultData.personTypeId || '',
                supplierNi: state.resultData.supplierNi || '',
                supplierCompanyName: state.resultData.supplierCompanyName || '',
                supplierSizeId: state.resultData.supplierSizeId || 0,
                countryCode: state.resultData.countryCode || '',
                subcontractingIndicator: state.resultData.subcontractingIndicator || false,
                meEppBenefitApplication: state.resultData.meEppBenefitApplication || false,
                srpClassificationOrder: state.resultData.srpClassificationOrder || 0,
                resultDate: state.resultData.resultDate || new Date(),
                legalNatureId: state.resultData.legalNatureId || '',
                situationId: state.resultData.situationId,
                descriptionBrand: (state.resultData as any).descriptionBrand || '',
                justification: 'Alteracao de registro de preco'
            };

            dispatch({
                type: ACTION_TYPES.setResultRequest,
                payload: updatePayload
            });
        } else {
            dispatch({ type: ACTION_TYPES.resetState });
        }
    }, [state.resultData, dispatch]);

    useEffect(() => {
        const loadLegalNatures = async () => {
            try {
                const natures = await BaseAppService.listLegalNature();
                dispatch({ type: ACTION_TYPES.setLegalNatures, payload: natures });
            } catch (error) {
                console.error('Erro ao carregar naturezas jurídicas:', error);
            }
        };

        if (state.isOpen) {
            loadLegalNatures();
        }
    }, [state.isOpen]);

    const handleOnChange = (e: any) => {
        const { name, value, type, checked } = e.target;

        const updates: any = {};
        updates[name] = type === 'checkbox' ? checked : value;

        dispatch({ type: ACTION_TYPES.setResultRequest, payload: updates });

        // Calcula o preço total quando quantidade ou preço unitário mudar
        if (name === 'approvedQuantity' || name === 'approvedUnitPrice') {
            const quantity = name === 'approvedQuantity' ? value : state.resultRequest.approvedQuantity;
            const unitPrice = name === 'approvedUnitPrice' ? value : state.resultRequest.approvedUnitPrice;
            const totalPrice = quantity * unitPrice;

            dispatch({
                type: ACTION_TYPES.setResultRequest,
                payload: { approvedTotalPrice: totalPrice }
            });
        }
    };

    const handleCurrencyChange = (value: string | undefined, name: string) => {
        const numericValue = parseFloat(value || '0');

        dispatch({
            type: ACTION_TYPES.setResultRequest,
            payload: { [name]: numericValue }
        });

        // Calcula o preço total quando quantidade ou preço unitário mudar
        if (name === 'approvedQuantity' || name === 'approvedUnitPrice') {
            const quantity = name === 'approvedQuantity' ? numericValue : state.resultRequest.approvedQuantity;
            const unitPrice = name === 'approvedUnitPrice' ? numericValue : state.resultRequest.approvedUnitPrice;
            const totalPrice = quantity * unitPrice;

            dispatch({
                type: ACTION_TYPES.setResultRequest,
                payload: { approvedTotalPrice: totalPrice }
            });
        }
    };

    const validateFields = (): boolean => {
        return !!(
            state.resultRequest.approvedQuantity > 0 &&
            state.resultRequest.approvedUnitPrice > 0 &&
            state.resultRequest.supplierNi &&
            state.resultRequest.supplierCompanyName &&
            state.resultRequest.supplierSizeId
        );
    };

    const handleSave = async () => {
        if (!validateFields()) {
            showMessage({ message: 'Preencha todos os campos obrigatórios!', type: 'error', open: true });
            return;
        }

        try {
            showLoading({ open: true });

            if (state.isEditMode && state.resultData) {
                await PurchaseAppService.updatePurchaseItemResult(
                    state.purchaseId,
                    state.itemNumber,
                    state.resultData.resultSequence,
                    state.resultRequest as IUpdatePurchaseItemResultRequest
                );
                showMessage({ message: 'Resultado alterado com sucesso!', type: 'success', open: true });
            } else {
                await PurchaseAppService.addPurchaseItemResult(
                    state.purchaseId,
                    state.itemNumber,
                    state.resultRequest as IAddPurchaseItemResultRequest
                );
                showMessage({ message: 'Resultado adicionado com sucesso!', type: 'success', open: true });
            }

            showLoading({ open: false });
            onSave();
            onClose();
        } catch (error) {
            showLoading({ open: false });
            showMessage({ message: 'Erro ao salvar resultado', type: 'error', open: true });
        }
    };

    return {
        handleOnChange,
        handleCurrencyChange,
        handleSave,
        validateFields
    };
};
